/**
 * B129 — Sold-property partial-FY rental income/expenses pro-rata.
 *
 * Per ITAA s.6-5 / s.8-1 + ATO RPG 2025 §"Selling a rental property":
 * income/expenses for a property sold mid-FY are claimable for the period
 * up to the date of disposal (saleContractDate, falling back to saleDate).
 *
 * The Day 37 wholesale "exclude sold properties" filter is correct for FYs
 * AFTER the sale, but wrong for the FY OF the sale — it drops the
 * partial-FY contribution to $0.
 *
 * `prorateFactor` is the SSOT predicate. All rental aggregation surfaces
 * (TaxPage, ReportsPage, paygVariationSavingsForFY, buildPortfolioContext)
 * route through this helper.
 *
 * Day-count semantics mirror Wave 2C Phase 11 (`computeDiv43AnnualClaim`):
 * - fyStartDate / fyEndDate from calculations.ts
 * - +1 inclusive day convention
 * - fixed /365 annualisation (no leap-year branching)
 *
 * For FY-of-sale: returns daysHeld / 365.
 * For active: returns 1.
 * For FY-after-sale (sold) or historical: returns 0.
 */

import type { PropertyRecord } from "../types/portfolio";
import { fyStartDate, fyEndDate, maxDate, minDate } from "./calculations";

export type SaleStatus = "active" | "sold-this-fy" | "sold-prior-fy";

/**
 * Pro-rata factor for a property's contribution to a given FY's rental
 * aggregations. Active = 1, FY-of-sale = days/365, FY-after = 0.
 */
export function prorateFactor(
  property: PropertyRecord,
  fyLabel: string,
): number {
  const status = property.status;

  // Active properties always contribute fully.
  if (!status || status === "active") return 1;

  // Historical: out of scope per BACKLOG B129 — V1 handles status='sold' only.
  if (status === "historical") return 0;

  // status === 'sold' from here on.
  const saleDateStr = property.saleContractDate ?? property.saleDate ?? null;

  // Defensive: sold property with null saleDate. Same behaviour as today
  // (wholesale exclusion). Not a regression.
  if (!saleDateStr) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        `[B129] Property ${property.id} has status='sold' but no saleDate/saleContractDate. Excluding from FY ${fyLabel} aggregation.`,
      );
    }
    return 0;
  }

  const fyStart = fyStartDate(fyLabel);
  const fyEnd = fyEndDate(fyLabel);
  const sale = new Date(saleDateStr + "T00:00:00Z");

  // FY-after-sale: sale was before this FY started.
  if (sale < fyStart) return 0;

  // FY-before-sale: shouldn't happen if status === 'sold' (would mean sale
  // is in the future and status was set prematurely), but treat as full FY
  // since the property was held throughout.
  if (sale > fyEnd) return 1;

  // FY-of-sale: clamp ownership window to the FY.
  const purchaseDateStr = property.contractDate ?? property.purchaseDate ?? null;
  const ownershipStart = purchaseDateStr
    ? new Date(purchaseDateStr + "T00:00:00Z")
    : fyStart;

  const claimStart = maxDate(fyStart, ownershipStart);
  const claimEnd = minDate(fyEnd, sale);

  if (claimEnd < claimStart) return 0;

  // +1 inclusive day convention (same as computeDiv43AnnualClaim).
  const daysHeld =
    Math.floor((claimEnd.getTime() - claimStart.getTime()) / 86400000) + 1;

  return daysHeld / 365;
}

/**
 * Categorical sale status for a given FY. Used by buildPortfolioContext
 * for AI annotation and UI suffix rendering.
 */
export function saleStatusForFY(
  property: PropertyRecord,
  fyLabel: string,
): SaleStatus {
  const status = property.status;
  if (!status || status === "active") return "active";

  const factor = prorateFactor(property, fyLabel);
  if (factor === 0) return "sold-prior-fy";
  return "sold-this-fy";
}
